import React, { useState, useEffect, useMemo } from 'react';
import {
	Button,
	Card,
	Checkbox,
	Dropdown,
	List,
	message,
	Modal,
	Select,
	Space,
	Table,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
	InventoryLocation,
	Material,
	MaterialLength,
	MaterialReservation,
} from '../../interfaces/MaterialInventory.interface';
import { IoIosUndo } from 'react-icons/io';
import {
	CheckOutlined,
	CloseOutlined,
	DeleteOutlined,
	DownOutlined,
	EditOutlined,
	HistoryOutlined,
	MoreOutlined,
	SwapOutlined,
} from '@ant-design/icons';
import MaterialHistoryDrawer from '../MaterialMovements/MaterialHistoryDrawer';

import { MaterialReservationStatus } from '../MaterialReservationStatus';
import {
	useDeleteMaterialLengthMutation,
	useDeleteMaterialReservationMutation,
	useGetInventoryLocationsQuery,
	useGetMaterialGroupedByLengthQuery,
	useGetMaterialShelvesQuery,
	useRecordAttributionCostMutation,
	useUpdateMaterialLengthMutation,
} from '../../redux/api/material.ts';
import { useQueryParameters } from '../../hooks';
import { locationSearchText, useLocationLabel } from '../../utils/locationLabel';
import {
	hasRecordedCuts,
	releaseErrorMessage,
	releaseTooltip,
} from '../../utils/materialReservationRelease';
import AddMaterialLengthsModal from './AddMaterialLengthsModal';
import MaterialReservationModal from '../MaterialReservationModal';
import { barReservationsEnabled } from '../MaterialReservationModal/maxCutQuantity';
import ModifyLengthsModal from '../MaterialLengths/ModifyLengthsModal';
import SplitLengthModal from './SplitLengthModal';
import MoveMaterialModal, { type MoveMaterialSummaryData } from '../MaterialLengths/MoveMaterialModal';
import MaterialLocationsSummaryModal from '../Operations/SawCutQueue/SawCutQueueTable/MaterialLocationsSummaryModal';
import { Lineitem } from '../../interfaces/Ordel.Interface';

interface Props {
	material: Material;
	refetchDataItem: () => void;
	refetchData: () => void;
	setSelectedMaterial: (material: Material) => void;
	setSelectedMaterialLength: (material_length: MaterialLength | null) => void;
	setSelectedMaterialReservation: (
		material_length: MaterialReservation
	) => void;
	// setSelectedMaterialDocument: (doc: any) => void;
	setIsMaterialReservationModalOpen: (value: boolean) => void;
	// selectedMaterialDocument: any;
	setIsReturnRemnantsModalOpen: (open: boolean) => void;
	showReturnRemnantButton: boolean;
	isMaterialReservationModalOpen: boolean;
	selectedMaterialLength: MaterialLength | null;
	loading: boolean;
	lineItem?: Lineitem;
	material_cut_length?: string | null;
	saw_kerf?: number;
	material_cut_quantity?: number;
}

const { Text } = Typography;

interface LocationSelectProps {
	materialLength: MaterialLength;
	materialShelves: Array<{ id: string; display_name: string }> | undefined;
	onUpdate: (
		id: string,
		shelf_id: string,
		materialLength: MaterialLength
	) => Promise<void>;
	onClose: () => void;
}

const LocationSelect: React.FC<LocationSelectProps> = ({
	materialLength,
	onUpdate,
	onClose,
}) => {
	const formatLocation = useLocationLabel();
	const { data: locationsData } = useGetInventoryLocationsQuery(
		{ material_length_id: materialLength?.id },
		{
			skip: !materialLength,
			refetchOnMountOrArgChange: true,
		}
	);

	const getCurrentValue = () => {
		if (!locationsData || locationsData.results.length === 0) {
			return { value: '', label: 'No Locations Available' };
		}

		const first_location = locationsData?.results[0];

		return {
			value: first_location.id,
			label: formatLocation(first_location.display_name),
		};
	};

	const [currentValue, setCurrentValue] = useState<{
		value: string;
		label: string;
	}>();

	// formatLocation changes when the label rules load: refresh the stored label too.
	useEffect(() => {
		if (locationsData) {
			setCurrentValue(getCurrentValue());
		}
	}, [materialLength, locationsData, formatLocation]);

	const handleChange = async (option: { value: string; label: string }) => {
		setCurrentValue(option);
	};

	const handleSubmit = async () => {
		if (currentValue) {
			await onUpdate(materialLength.id, currentValue.value, materialLength);
			onClose();
		}
	};

	return (
		<>
			<div className={'pt-4'}>
				<label className="mr-2">Select New Location:</label>
				<Select
					showSearch={true}
					labelInValue={true}
					popupMatchSelectWidth={true}
					optionFilterProp="searchText"
					options={locationsData?.results.map((record: InventoryLocation) => {
						return {
							value: record.id,
							label: formatLocation(record.display_name),
							searchText: locationSearchText(record.display_name, formatLocation),
						};
					})}
					onChange={handleChange}
					value={currentValue}
					style={{ width: '100%' }}
				/>
			</div>
			<div className="flex justify-end gap-2 pt-4">
				<Button onClick={onClose}>Cancel</Button>
				<Button
					type="primary"
					onClick={handleSubmit}
					disabled={locationsData?.results.length === 0}
				>
					Confirm
				</Button>
			</div>
		</>
	);
};

const MaterialInventoryLengths = (props: Props) => {
	const navigate = useNavigate();
	const formatLocation = useLocationLabel();
	const queryParams = useQueryParameters();

	const { material, loading } = props;
	const [updateMaterialLength] = useUpdateMaterialLengthMutation();
	const [deleteMaterialLength] = useDeleteMaterialLengthMutation();
	const [deleteMaterialReservation] = useDeleteMaterialReservationMutation();
	const [recordAttributionCost] = useRecordAttributionCostMutation();

	const materialShelvesQuery = useGetMaterialShelvesQuery();

	const groupedLengthQuery = useGetMaterialGroupedByLengthQuery({
		material_id: material.id,
	});

	const materialBuilding = useMemo(() => {
		for (const l of material?.lengths || []) {
			const b = (l as any).inventory_location?.shelf?.rack?.building;
			if (b?.id) return { id: b.id as string, name: b.name as string };
		}
		const name = material?.material_lengths?.find((s) => s.building_name)?.building_name;
		return name ? { id: undefined, name } : null;
	}, [material]);

	const [groupMaterialLengths, setGroupMaterialLengths] = useState<boolean>(
		Boolean(queryParams.get('group_by_length')) || false
	);
	const [showAllReservations, setShowAllReservations] =
		useState<boolean>(false);
	const [isAddMaterialLengthsModalOpen, setIsAddMaterialLengthsModalModalOpen] =
		useState<boolean>(false);
	const [isMaterialReservationModalOpen, setIsMaterialReservationModalOpen] =
		useState<boolean>(false);
	const [isMaterialMoveLocationModalOpen, setIsMaterialMoveLocationModalOpen] =
		useState<boolean>(false);
	const [selectedMaterialReservation, setSelectedMaterialReservation] =
		useState<MaterialReservation | null>(null);
	const [selectedMaterialLength, setSelectedMaterialLength] =
		useState<MaterialLength | null>(null);

	const [isSplitLengthModalOpen, setIsSplitLengthModalOpen] =
		useState<boolean>(false);
	const [selectedSplitLength, setSelectedSplitLength] =
		useState<MaterialLength | null>(null);
	const [isModifyLengthsModalOpen, setIsModifyLengthsModalOpen] =
		useState<boolean>(false);
	const [showMoveSummary, setShowMoveSummary] = useState(false);
	const [historyLength, setHistoryLength] = useState<MaterialLength | null>(null);
	const [moveSummaryData, setMoveSummaryData] = useState<MoveMaterialSummaryData | null>(null);

	async function handleUpdateMaterialReservation(
		material_length: MaterialLength,
		reservation: MaterialReservation
	) {
		setSelectedMaterialReservation(reservation);
		setIsMaterialReservationModalOpen(true);
		setSelectedMaterialLength(material_length);
	}

	const handleUpdateMaterialLength = async (
		id: string,
		location_id: string,
		_materialLength: MaterialLength
	) => {
		await updateMaterialLength({
			id: id,
			values: { inventory_location: location_id },
		})
			.unwrap()
			.then(() => {
				props.refetchDataItem();
			});
	};

	const showReleaseConfirm = (
		reservation_id: string,
		order_line_item_id?: string
	) => {
		return Modal.confirm({
			title: `Release reservation?`,
			content:
				'This will remove the reservation and release the material back into inventory.',
			okText: 'Yes',
			cancelText: 'No',
			okButtonProps: {
				type: 'default',
				icon: <CheckOutlined />,
			},
			cancelButtonProps: {
				type: 'default',
				icon: <CloseOutlined />,
			},
			onOk() {
				deleteMaterialReservation({
					id: reservation_id,
					orderLineItemId: order_line_item_id,
				})
					.unwrap()
					.then(() => {
						props.refetchData();
						props.refetchDataItem();
					})
					.catch((error) => {
						// The API refuses a release once cuts are recorded. Report
						// its reason rather than leaving the dialog to close on a
						// release that did not happen.
						message.error(releaseErrorMessage(error));
					});
			},
			onCancel() {
				console.log('Action canceled');
			},
		});
	};

	const grouped_lengths = groupedLengthQuery.data ?? [];

	function handleCloseMaterialReservationModal() {
		setIsMaterialReservationModalOpen(false);
		// setSelectedMaterial(null);
		setSelectedMaterialLength(null);
	}

	const handleCloseModifyLengthsModal = () => {
		setIsModifyLengthsModalOpen(false);
		props.setSelectedMaterialLength(null);
	};

	return (
		<>
			<Card
				title={
					<div className="flex flex-row w-full justify-between items-center">
						<div className="flex flex-row gap-2">
							<div>Material Lengths</div>
						</div>
						<div className="flex flex-row items-center gap-1">
							<Button
								onClick={() => {
									setIsAddMaterialLengthsModalModalOpen(true);
								}}
							>
								Add Lengths
							</Button>

							{/*{props.showReturnRemnantButton ? (*/}
							{/*	<Button*/}
							{/*		icon={<IoIosUndo />}*/}
							{/*		type="primary"*/}
							{/*		onClick={() => {*/}
							{/*			props.setSelectedMaterial(material);*/}
							{/*			props.setIsReturnRemnantsModalOpen(true);*/}
							{/*		}}*/}
							{/*	>*/}
							{/*		Return Remnant*/}
							{/*	</Button>*/}
							{/*) : null}*/}
						</div>
					</div>
				}
				styles={{
					body: { padding: '1rem' },
					title: { width: '100%' },
				}}
			>
				<div className="flex flex-row gap-2 mb-3">
					<Checkbox
						checked={groupMaterialLengths}
						onChange={(e) => {
							setGroupMaterialLengths(e.target.checked);
							if (e.target.checked) {
								queryParams.set('group_by_length', 'True');
							} else {
								queryParams.delete('group_by_length');
							}
							navigate({ search: queryParams.toString() });
						}}
					>
						Group by length
					</Checkbox>
					<Checkbox
						checked={showAllReservations}
						onChange={(e) => {
							setShowAllReservations(e.target.checked);
						}}
					>
						Show all reservations
					</Checkbox>
				</div>
				<Table
					loading={loading}
					size="small"
					scroll={{ x: '720px' }}
					rowKey={(record) => record.id ?? record.current_length}
					onRow={(record: MaterialLength) => (
						groupMaterialLengths
							? {}
							: {
								'data-testid': 'fastlane-material-length-row',
								'data-material-length-id': record.id,
								'data-material-length-location':
									record.inventory_location?.code ||
									record.inventory_location?.display_name ||
									record.inventory_location?.id ||
									'',
								'data-inventory-location-id':
									record.inventory_location?.id || '',
							} as React.HTMLAttributes<HTMLElement>
					)}
					columns={
						groupMaterialLengths
							? [
								{
									title: 'Quantity',
									width: 100,
									render: (record) => record.count,
								},
								{
									title: 'Bar Length',
									render: (record) => `${record.current_length} in.`,
								},
								{
									title: 'Total Length',
									render: (record) =>
										`${Number(record.total_current_length).toFixed(1)} in.`,
								},
								{
									title: 'Total Available Length',
									dataIndex: 'total_available_length',
									render: (record) => (
										<Text type={record > 0 ? 'success' : 'danger'}>{`${Number(
											record
										).toFixed(1)} in.`}</Text>
									),
								},
								{
									title: 'Reservations',
									render: (record) => record.reservation_count,
								},
							]
							: [
								{
									title: 'Status',
									width: '5%',
									render: (record: MaterialLength) => (
										<Tag
											color={
												record.status === 'Awaiting Delivery'
													? 'yellow'
													: record.status === 'In Stock'
														? 'green'
														: record.status === 'Quality Hold'
														? 'orange'
														: record.status === 'Scrapped'
														? 'red'
														: 'default'
												}
											>
												{record.status}
											</Tag>
										),
										key: 'status',
									},
									{
										title: 'Bundle',
										width: '5%',
										dataIndex: 'bundle',
									},
									{
										title: 'ID',
										width: '3%',
										dataIndex: 'friendly_id',
										render: (record) => `${record}`,
									},
									{
										title: 'Location',
										width: 250,
										render: (record: MaterialLength) => (
											<span
												data-testid="fastlane-material-length-location"
												data-material-length-id={record.id}
												data-material-length-location={
													record.inventory_location?.code ||
													record.inventory_location?.display_name ||
													record.inventory_location?.id ||
													''
												}
												data-inventory-location-id={
													record.inventory_location?.id || ''
												}
											>
												{record.inventory_location ? (
													<Link to={`/admin/locations/manage?building=${record.inventory_location.shelf.rack.building.id}&rack=${record.inventory_location.shelf.rack.rack_code}&location=${record.inventory_location.code}`}>
														{formatLocation(record.inventory_location.display_name)}
													</Link>
												) : (
													<Text type="secondary">No Location Assigned</Text>
												)}
												{record.overflow_message ? (
													<div>
														<Text type="secondary" style={{ fontSize: 12 }}>
															{record.overflow_message}
														</Text>
													</div>
												) : null}
											</span>
										),
									},
									{
										title: 'Purchased Length (IN)',
										dataIndex: 'purchased_length',
										width: '6%',
										render: (record) => (record != null ? Number(record) : 'Unknown'),
									},
									{
										title: 'Current Length (IN)',
										dataIndex: 'current_length',
										width: '6%',
										render: (record, material_length: MaterialLength) => (
											<div className="flex flex-row gap-2 items-center">
												{Number(record)}
												<Tooltip title="Edit the current length">
													<EditOutlined
														onClick={() => {
															props.setSelectedMaterialLength(material_length);
															setIsModifyLengthsModalOpen(true);
														}}
													/>
												</Tooltip>
											</div>
										),
									},
								{
										title: 'Current Weight (LBS)',
										dataIndex: 'current_weight',
										width: '6%',
										render: (record) => (record ? Number(record) : '0'),
								},
									{
										title: 'Total Available Length (IN)',
										dataIndex: 'total_available_length',
										render: (record) => (
											<Text type={record > 0 ? 'success' : 'danger'}>
												{Number(record)}
											</Text>
										),
									},
									{
										title: 'Reservations',
										dataIndex: 'material_reservations',
										render: (
											reservations: MaterialReservation[],
											material_length: MaterialLength
										) => (
											<>
												{reservations.length > 0 ? (
													<List
														itemLayout="horizontal"
														dataSource={
															showAllReservations
																? reservations
																: reservations.filter(
																		(x) => x.material_cut_status !== 'Cut'
																  )
														}
														size="small"
														locale={{
															emptyText: <>No Active Reservations</>,
														}}
														renderItem={(reservation: MaterialReservation) => (
															<List.Item
																key={reservation.id}
																style={{ padding: 0 }}
																extra={
																	<Dropdown
																		menu={{
																			items: [
																				{
																					key: '1',
																					label: 'Edit',
																					onClick: () =>
																						handleUpdateMaterialReservation(
																							material_length,
																							reservation
																						),
																					disabled:
																						hasRecordedCuts(reservation),
																				},
																				{
																					key: '2',
																					label: (
																						<Tooltip
																							title={releaseTooltip(reservation)}
																						>
																							Release
																						</Tooltip>
																					),
																					onClick: () =>
																						showReleaseConfirm(
																							reservation.id,
																							reservation?.order_line_item?.id
																						),
																					disabled:
																						hasRecordedCuts(reservation),
																				},
																			],
																		}}
																		trigger={['click']}
																		placement="bottomRight"
																	>
																		<Button
																			type="text"
																			icon={<MoreOutlined />}
																		/>
																	</Dropdown>
																}
															>
																<div className="w-full">
																	<Tooltip
																		title={`Quantity ${reservation.material_cut_quantity} | Cut length ${reservation.material_cut_length}`}
																	>
																		<div className="flex flex-row gap-2 justify-between items-center">
																			<div>
																				{` ${reservation.reserved_length} inches Reserved for `}
																				{reservation?.order_line_item ? (
																					<Link
																						to={`/orders/line-items/${reservation?.order_line_item?.id}`}
																					>
																						{`${reservation?.order_line_item?.name} (${reservation?.order_line_item?.order?.name})`}
																					</Link>
																				) : (
																					reservation.reserved_for_description
																				)}
																			</div>
																			<MaterialReservationStatus
																				reservation={reservation}
																				refetch={() => {
																					props.refetchData();
																					props?.refetchDataItem();
																				}}
																			/>
																		</div>
																	</Tooltip>
																</div>
															</List.Item>
														)}
													/>
												) : null}
											</>
										),
									},
									{
										title: 'Actions',
										render: (material_length) => {
											const reservationsOff = !barReservationsEnabled(
												material_length.inventory_location
											);
											return (
											<Space.Compact>
												<Tooltip
													title={
														reservationsOff
															? 'Reservations off for this rack/shelf'
															: undefined
													}
												>
													<span>
														<Button
															type="default"
															disabled={
																material_length.total_available_length ===
																	0 || reservationsOff
															}
															onClick={() => {
																setSelectedMaterialReservation(null);
																setSelectedMaterialLength(material_length);
																setIsMaterialReservationModalOpen(true);
															}}
														>
															Reserve
														</Button>
													</span>
												</Tooltip>

												<Dropdown
													menu={{
														items: [
															{
																key: '0',
																icon: <SwapOutlined />,
																label: 'Move',
																onClick: () => {
																	setSelectedMaterialLength(material_length);
																	setIsMaterialMoveLocationModalOpen(true);
																},
															},
															{
																key: 'split',
																icon: <EditOutlined />,
																label: 'Split Length',
																onClick: () => {
																	setSelectedSplitLength(material_length);
																	setIsSplitLengthModalOpen(true);
																},
															},
															{
																key: 'history',
																icon: <HistoryOutlined />,
																label: 'Material History',
																onClick: () => setHistoryLength(material_length),
															},
															{
																key: '1',
																icon: <DeleteOutlined />,
																label: 'Delete',
																danger: true,
																disabled:
																	material_length.material_reservations.length >
																	0,
																onClick: () => {
																	deleteMaterialLength({
																		id: material_length.id,
																	})
																		.unwrap()
																		.then(() => {
																			props.refetchDataItem();
																			props.refetchData();
																		});
																},
															},
														],
													}}
												>
													<Button type="default" icon={<DownOutlined />} />
												</Dropdown>
											</Space.Compact>
											);
										},
									},
							  ]
					}
					dataSource={groupMaterialLengths ? grouped_lengths : material.lengths}
				/>
			</Card>
			{isAddMaterialLengthsModalOpen && (
			<AddMaterialLengthsModal
				isAddMaterialLengthsModalOpen={isAddMaterialLengthsModalOpen}
				onClose={() => {
					setIsAddMaterialLengthsModalModalOpen(false);
				}}
				material={material as Material}
				refetchData={() => {
					props.refetchDataItem();
					props?.refetchData();
				}}
				building_id={materialBuilding?.id}
				building_name={materialBuilding?.name ?? undefined}
			/>
			)}

			{isMaterialReservationModalOpen && (
				<MaterialReservationModal
					lineItem={props.lineItem}
					material_cut_length={props.material_cut_length}
					saw_kerf={props?.saw_kerf}
					material_cut_quantity={props?.material_cut_quantity}
					onClose={handleCloseMaterialReservationModal}
					selectedMaterialReservation={selectedMaterialReservation}
					selectedMaterial={material}
					selectedMaterialLength={selectedMaterialLength}
					isMaterialReservationModalOpen={isMaterialReservationModalOpen}
					reserveMultipleLengths={false}
					refetchData={() => {
						props.refetchDataItem(), props?.refetchData();
					}}
				/>
			)}
			{isSplitLengthModalOpen && selectedSplitLength && (
				<SplitLengthModal
					isSplitLengthModalOpen={isSplitLengthModalOpen}
					onClose={() => {
						setIsSplitLengthModalOpen(false);
						setSelectedSplitLength(null);
					}}
					material={material}
					selectedMaterialLength={selectedSplitLength}
					refetchData={() => {
						props.refetchDataItem();
						props.refetchData();
					}}
				/>
			)}

			{isMaterialMoveLocationModalOpen && selectedMaterialLength && (
				<MoveMaterialModal
					open={isMaterialMoveLocationModalOpen}
					onClose={() => {
						setIsMaterialMoveLocationModalOpen(false);
						setSelectedMaterialLength(null);
					}}
					selectedLengths={[{
						...selectedMaterialLength,
						material_type: material.type,
						material_shape: material.shape,
						height: material.height,
						width: material.width,
						outer_diameter: material.outer_diameter,
						inner_diameter: material.inner_diameter,
						wall_thickness: material.wall_thickness,
					}]}
					shelves={materialShelvesQuery.data}
					onConfirmSuccess={(summaryData) => {
						setMoveSummaryData(summaryData);
						setShowMoveSummary(true);
					}}
					refetch={() => {
						props.refetchData();
						props.refetchDataItem();
						groupedLengthQuery.refetch();
					}}
				/>
			)}

			{showMoveSummary && moveSummaryData && (
				<MaterialLocationsSummaryModal
					visible={showMoveSummary}
					onComplete={() => {
						setShowMoveSummary(false);
						setMoveSummaryData(null);
					}}
					materialInfo={moveSummaryData.materialInfo}
					lengths={moveSummaryData.lengths}
					title="Material Locations Summary"
					source="receiving"
				/>
			)}

			<ModifyLengthsModal
				open={isModifyLengthsModalOpen}
				onClose={() => {
					handleCloseModifyLengthsModal();
					props.refetchDataItem();
					props.refetchData();
				}}
				selectedLengths={
					props.selectedMaterialLength
						? [
								{
									...props.selectedMaterialLength,
									material_type: material.type,
									material_shape: material.shape,
									height: material.height,
									width: material.width,
									outer_diameter: material.outer_diameter,
									inner_diameter: material.inner_diameter,
									wall_thickness: material.wall_thickness,
								},
							]
						: []
				}
				onConfirm={async (operatorId, modifications) => {
					try {
						await recordAttributionCost({
							operator_id: String(operatorId),
							modifications,
						}).unwrap();
					} catch (err) {
						console.error('Failed to record attribution cost:', err);
					}
				}}
			/>

			<MaterialHistoryDrawer
				materialLengthId={historyLength?.id ?? null}
				friendlyId={historyLength?.friendly_id}
				open={Boolean(historyLength)}
				onClose={() => setHistoryLength(null)}
			/>
		</>
	);
};

export default MaterialInventoryLengths;
